import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import api from "../lib/api";
import { X, Search, Clock, Send } from "@/components/icons";

/**
 * ReferralQuickModal — modal rápido para enviar un referido a otro doctor
 * desde la ventana de chat. Muestra los últimos 5 pacientes atendidos como
 * acceso rápido + un buscador para encontrar cualquier paciente.
 */
export default function ReferralQuickModal({ toDoctor, onClose, onSent }) {
  const [recent, setRecent] = useState([]);
  const [all, setAll] = useState([]);
  const [q, setQ] = useState("");
  const [selected, setSelected] = useState(null);
  const [reason, setReason] = useState("");
  const [notes, setNotes] = useState("");
  const [urgency, setUrgency] = useState("rutina");
  const [sending, setSending] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const [a, b] = await Promise.all([
          api.get("/patients/recientes", { params: { limit: 5 } }),
          api.get("/patients"),
        ]);
        setRecent(a.data || []);
        setAll(b.data || []);
      } catch {
        toast.error("No se pudieron cargar los pacientes");
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const filtered = useMemo(() => {
    if (!q.trim()) return [];
    const query = q.trim().toLowerCase();
    return all
      .filter((p) =>
        (p.full_name || "").toLowerCase().includes(query) ||
        (p.document_id || "").toLowerCase().includes(query) ||
        (p.phone || "").toLowerCase().includes(query)
      )
      .slice(0, 10);
  }, [q, all]);

  const send = async (e) => {
    e?.preventDefault?.();
    if (!selected || !reason.trim() || sending) return;
    setSending(true);
    try {
      const { data } = await api.post("/referrals", {
        patient_id: selected.id,
        to_doctor_id: toDoctor.id,
        reason: reason.trim(),
        clinical_notes: notes.trim() || "",
        urgency,
      });
      toast.success("Referido enviado");
      onSent?.(data);
      // Abrir vista imprimible en nueva pestaña
      window.open(`/referidos/${data.id}/imprimir`, "_blank", "noopener");
      onClose?.();
    } catch (err) {
      const msg = err?.response?.data?.detail || "No se pudo enviar el referido";
      toast.error(typeof msg === "string" ? msg : "Error al enviar");
    } finally {
      setSending(false);
    }
  };

  const initials = (name) =>
    (name || "?")
      .split(" ")
      .map((s) => s[0])
      .filter(Boolean)
      .slice(0, 2)
      .join("")
      .toUpperCase();

  const PatientRow = ({ p, isRecent }) => (
    <button
      type="button"
      data-testid={`referral-patient-${p.id}`}
      onClick={() => setSelected(p)}
      className={`w-full text-left px-3 py-2.5 rounded-xl flex items-center gap-3 transition border ${
        selected?.id === p.id
          ? "bg-[#0071E3]/[0.06] border-[#0071E3]/40"
          : "bg-white hover:bg-black/[0.03] border-black/[0.06]"
      }`}
    >
      <span className="w-9 h-9 rounded-full flex-shrink-0 bg-gradient-to-br from-[#D7D4FF] to-[#F7D6EC] flex items-center justify-center text-white font-bold text-[11px]">
        {p.photo_url ? (
          <img src={p.photo_url} alt={p.full_name} className="w-full h-full rounded-full object-cover" />
        ) : (
          initials(p.full_name)
        )}
      </span>
      <div className="flex-1 min-w-0">
        <div className="text-[13.5px] font-semibold text-black truncate">
          {p.full_name}
        </div>
        <div className="text-[11px] text-black/50 truncate">
          {p.document_id || p.phone || "—"}
        </div>
      </div>
      {isRecent && (
        <span className="inline-flex items-center gap-1 text-[9.5px] font-semibold text-black/50 bg-black/[0.04] px-1.5 py-0.5 rounded">
          <Clock className="w-3 h-3" strokeWidth={1.75} weight="regular" />
          Reciente
        </span>
      )}
    </button>
  );

  return (
    <div
      className="fixed inset-0 z-[70] flex items-center justify-center bg-black/40 backdrop-blur-md p-4"
      onClick={onClose}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        className="bg-white rounded-[28px] w-full max-w-md shadow-[0_24px_64px_rgba(0,0,0,0.24)] flex flex-col max-h-[88vh] overflow-hidden"
        data-testid="referral-quick-modal"
      >
        {/* Header */}
        <div className="px-5 pt-5 pb-3 flex items-start justify-between">
          <div>
            <div className="text-[11px] uppercase tracking-wider text-black/50 font-semibold">
              Enviar referido a
            </div>
            <div className="text-[17px] font-bold text-black leading-tight mt-0.5">
              {toDoctor.name}
            </div>
            {toDoctor.specialty && (
              <div className="text-[12px] text-black/55 mt-0.5">
                {toDoctor.specialty}
              </div>
            )}
          </div>
          <button
            type="button"
            onClick={onClose}
            data-testid="referral-modal-close"
            className="w-9 h-9 rounded-xl flex items-center justify-center text-black/50 hover:bg-black/[0.05] hover:text-black transition"
          >
            <X className="w-4 h-4" strokeWidth={1.75} weight="regular" />
          </button>
        </div>

        {!selected ? (
          <>
            {/* Search */}
            <div className="px-5 pb-3">
              <div className="relative">
                <Search
                  className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-black/40"
                  strokeWidth={1.75}
                  weight="regular"
                />
                <input
                  autoFocus
                  type="text"
                  value={q}
                  onChange={(e) => setQ(e.target.value)}
                  data-testid="referral-search-input"
                  placeholder="Buscar paciente por nombre, cédula o teléfono…"
                  className="w-full h-11 pl-10 pr-3 rounded-full bg-[#F2F3F5] text-[13px] text-black placeholder-black/40 focus:outline-none focus:ring-2 focus:ring-[#0071E3]/30 transition"
                />
              </div>
            </div>

            {/* Lista */}
            <div className="flex-1 overflow-y-auto px-5 pb-5 space-y-4">
              {loading ? (
                <div className="text-center text-black/40 text-sm py-10">
                  Cargando…
                </div>
              ) : q.trim() ? (
                <div className="space-y-1.5">
                  <div className="text-[10px] uppercase tracking-wider font-semibold text-black/40 px-1">
                    Resultados · {filtered.length}
                  </div>
                  {filtered.length === 0 ? (
                    <div className="text-center text-black/40 text-sm py-6">
                      Ningún paciente coincide con “{q}”
                    </div>
                  ) : (
                    filtered.map((p) => <PatientRow key={p.id} p={p} />)
                  )}
                </div>
              ) : (
                <>
                  <div className="space-y-1.5">
                    <div className="text-[10px] uppercase tracking-wider font-semibold text-black/40 px-1">
                      Últimos pacientes atendidos
                    </div>
                    {recent.length === 0 ? (
                      <div className="text-[12px] text-black/40 italic px-1 py-2">
                        Aún no has terminado consultas.
                      </div>
                    ) : (
                      recent.map((p) => (
                        <PatientRow key={p.id} p={p} isRecent />
                      ))
                    )}
                  </div>
                </>
              )}
            </div>
          </>
        ) : (
          /* Form del referido */
          <form onSubmit={send} className="flex-1 flex flex-col overflow-hidden">
            <div className="px-5 pb-3 flex items-center gap-3 border-b border-black/[0.06]">
              <span className="w-10 h-10 rounded-full flex-shrink-0 bg-gradient-to-br from-[#D7D4FF] to-[#F7D6EC] flex items-center justify-center text-white font-bold text-[12px]">
                {initials(selected.full_name)}
              </span>
              <div className="flex-1 min-w-0">
                <div className="text-[13.5px] font-semibold text-black truncate">
                  {selected.full_name}
                </div>
                <div className="text-[11px] text-black/50 truncate">
                  {selected.document_id || selected.phone || "—"}
                </div>
              </div>
              <button
                type="button"
                onClick={() => setSelected(null)}
                className="text-[12px] font-semibold text-[#0071E3] hover:underline"
                data-testid="referral-change-patient"
              >
                Cambiar
              </button>
            </div>

            <div className="flex-1 overflow-y-auto px-5 py-4 space-y-3">
              <div>
                <label className="text-[11px] uppercase tracking-wider font-semibold text-black/50 block mb-1">
                  Motivo del referido *
                </label>
                <input
                  autoFocus
                  type="text"
                  value={reason}
                  onChange={(e) => setReason(e.target.value)}
                  data-testid="referral-reason-input"
                  placeholder="Ej. Evaluación cardiológica por soplo"
                  className="w-full h-11 px-3 rounded-xl bg-[#F2F3F5] text-[13px] text-black placeholder-black/40 focus:outline-none focus:ring-2 focus:ring-[#0071E3]/30 transition"
                  required
                  minLength={3}
                  maxLength={500}
                />
              </div>
              <div>
                <label className="text-[11px] uppercase tracking-wider font-semibold text-black/50 block mb-1">
                  Notas clínicas (opcional)
                </label>
                <textarea
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                  data-testid="referral-notes-input"
                  rows={3}
                  placeholder="Antecedentes, hallazgos, medicación actual…"
                  className="w-full px-3 py-2 rounded-xl bg-[#F2F3F5] text-[13px] text-black placeholder-black/40 focus:outline-none focus:ring-2 focus:ring-[#0071E3]/30 transition resize-none"
                />
              </div>
              <div>
                <label className="text-[11px] uppercase tracking-wider font-semibold text-black/50 block mb-1">
                  Urgencia
                </label>
                <div className="flex gap-2">
                  {[
                    { v: "rutina", l: "Rutina" },
                    { v: "urgente", l: "Urgente" },
                  ].map((o) => (
                    <button
                      key={o.v}
                      type="button"
                      data-testid={`referral-urgency-${o.v}`}
                      onClick={() => setUrgency(o.v)}
                      className={`flex-1 h-10 rounded-xl text-[12.5px] font-semibold transition border ${
                        urgency === o.v
                          ? o.v === "urgente"
                            ? "bg-red-500 border-red-500 text-white"
                            : "bg-black border-black text-white"
                          : "bg-white border-black/[0.08] text-black/60 hover:border-black/20"
                      }`}
                    >
                      {o.l}
                    </button>
                  ))}
                </div>
              </div>
            </div>

            <div className="px-5 py-3 border-t border-black/[0.06] flex items-center gap-2">
              <button
                type="button"
                onClick={onClose}
                className="flex-1 h-11 rounded-full bg-white border border-black/[0.08] text-[13px] font-semibold text-black/70 hover:bg-black/[0.03] transition"
              >
                Cancelar
              </button>
              <button
                type="submit"
                disabled={!reason.trim() || sending}
                data-testid="referral-send-button"
                className="flex-[1.5] h-11 rounded-full bg-[#0071E3] text-white text-[13px] font-bold hover:bg-[#0077ED] disabled:opacity-50 transition inline-flex items-center justify-center gap-2"
              >
                <Send className="w-4 h-4" strokeWidth={2} weight="regular" />
                {sending ? "Enviando…" : "Enviar e imprimir"}
              </button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
}
